'use client';

import { contacts } from '@/_config';
import { useRouter } from 'next/navigation';
// import { Sidebar } from 'primereact/sidebar';
import { useState, useEffect } from 'react';
import { UserMenu } from './UserMenu';
import { CartButton } from './CartButton';
import { logout } from '@/lib/logout';

const parseCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
};

const sideNavLinks = [
    { id: 'home', label: 'Home', icon: 'pi pi-home', url: '/' },
    { id: 'products', label: 'Products', icon: 'pi pi-box', url: '/products' },
    { id: 'about', label: 'About Us', icon: 'pi pi-info-circle', url: '/about-us' },
    { id: 'contact', label: 'Contact Us', icon: 'pi pi-envelope', url: '/contactus' },
    { id: 'warranty', label: 'Warranty', icon: 'pi pi-shield', url: '/warranty' },
    { id: 'oowarranty', label: 'Out of Warranty', icon: 'pi pi-wrench', url: '/out-of-warranty' },
    { id: 'support', label: 'Support', icon: 'pi pi-headphones', url: '/support' },
    { id: 'privacy', label: 'Privacy Policy', icon: 'pi pi-lock', url: '/privacy-policy' },
    { id: 'tos', label: 'Terms of Service', icon: 'pi pi-file', url: '/terms-of-service' },
    { id: 'tosale', label: 'Terms of Sale', icon: 'pi pi-file-edit', url: '/terms-of-sale' },
];

const accountMenuItems = [
    { label: 'My Orders', icon: 'pi pi-shopping-bag', url: '/account/orders' },
    { label: 'My Support', icon: 'pi pi-headphones', url: '/account/support' },
    { label: 'Saved Address', icon: 'pi pi-map-marker', url: '/account/addresses' },
    { label: 'Settings', icon: 'pi pi-cog', url: '/account/settings' },
];

export const MenuList = () => {
    const router = useRouter();
    const [visible, setVisible] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [authUser, setAuthUser] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const syncAuthFromCookie = () => {
            const raw = parseCookie('authUser');
            if (raw) {
                try {
                    setAuthUser(JSON.parse(raw));
                    setIsLoggedIn(true);
                    return;
                } catch { /* malformed cookie */ }
            }
            setAuthUser(null);
            setIsLoggedIn(false);
        };

        syncAuthFromCookie();
        // Login flows that don't do a full page navigation (e.g. Google One Tap)
        // dispatch this so the header updates instantly instead of staying stale.
        window.addEventListener('ennix:auth-changed', syncAuthFromCookie);
        return () => window.removeEventListener('ennix:auth-changed', syncAuthFromCookie);
    }, []);

    // The authUser cookie is only written at login time, so it can go stale —
    // e.g. a Google-sourced avatar saved to the DB after that login never
    // shows up here until the next full re-login. Refresh it once per mount
    // from the live profile so the header avatar reflects the DB.
    useEffect(() => {
        if (!isLoggedIn) return;
        fetch('/api/user/profile')
            .then((r) => r.json())
            .then((json) => {
                if (!json.success) return;
                const d = json.data;
                setAuthUser((prev) => {
                    if (!prev) return prev;
                    const updated = {
                        ...prev,
                        firstName: d?.profile?.firstName ?? prev.firstName,
                        lastName: d?.profile?.lastName ?? prev.lastName,
                        profileImage: d?.profile?.profileImage || prev.profileImage || null,
                    };
                    document.cookie = `authUser=${encodeURIComponent(JSON.stringify(updated))}; path=/; max-age=${10 * 24 * 60 * 60}`;
                    return updated;
                });
            })
            .catch(() => {});
    }, [isLoggedIn]);

    const doAction = (url) => {
        setVisible(false);
        if (!url.includes('#')) {
            router.push(url, { scroll: true });
        } else {
            router.push(`/${url}`, { scroll: true });
        }
    };

    const handleLogout = async () => {
        await logout();
        setIsLoggedIn(false);
        setAuthUser(null);
        setVisible(false);
        router.push('/');
    };

    const handleSideNavClick = (link) => {
        if (link.external) {
            setVisible(false);
            window.open(link.url, '_blank');
        } else {
            doAction(link.url);
        }
    };

    return (
        <>
            <div className="flex align-items-center  gap-2">
                <CartButton loading={loading} />
                <UserMenu
                    loading={loading}
                    isLoggedIn={isLoggedIn}
                    authUser={authUser}
                    onNavigate={doAction}
                    onLogout={handleLogout}
                />
            </div>

        </>
    );
};
